import React from 'react'

const S  = 'rgba(43,43,43,0.07)'
const A  = 'rgba(27,123,63,0.18)'
const A2 = 'rgba(27,123,63,0.06)'

/**
 * Absolute-positioned architectural SVG background.
 * Wrap a section in position:relative and drop this inside.
 * variant: 'building' | 'skyline' | 'blueprint' | 'crane'
 */
export default function ArchLineBg({ variant = 'building', opacity = 1, flip = false }) {
  const style = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity,
    transform: flip ? 'scaleX(-1)' : undefined,
    overflow: 'hidden',
  }

  if (variant === 'building') return (
    <div style={style}>
      <svg viewBox="0 0 1400 600" preserveAspectRatio="xMaxYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ab-dot" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="12" cy="12" r="0.6" fill={S} />
          </pattern>
        </defs>
        <rect width="1400" height="600" fill="url(#ab-dot)" />

        {/* Tall building right side */}
        <rect x="900" y="40" width="220" height="540" fill="none" stroke={A} strokeWidth="1" />
        {/* window grid */}
        {Array.from({length:12}).map((_,r) =>
          Array.from({length:4}).map((_,c) => (
            <rect key={`${r}-${c}`}
              x={910 + c*52} y={52 + r*44}
              width={38} height={30}
              fill={A2} stroke={S} strokeWidth="0.4" />
          ))
        )}
        {/* floor lines */}
        {Array.from({length:11}).map((_,i) => (
          <line key={i} x1="900" y1={84+i*44} x2="1120" y2={84+i*44} stroke={S} strokeWidth="0.4" />
        ))}
        {/* columns */}
        {[900,953,1007,1061,1120].map(x => (
          <line key={x} x1={x} y1="40" x2={x} y2="580" stroke={S} strokeWidth="0.4" strokeDasharray="2 5" />
        ))}

        {/* Shorter building right */}
        <rect x="1160" y="200" width="140" height="380" fill="none" stroke={S} strokeWidth="0.7" />
        {Array.from({length:7}).map((_,r) =>
          Array.from({length:2}).map((_,c) => (
            <rect key={`${r}-${c}`}
              x={1170 + c*62} y={212 + r*52}
              width={44} height={36}
              fill={A2} stroke={S} strokeWidth="0.35" />
          ))
        )}

        {/* Small building far right */}
        <rect x="1330" y="320" width="80" height="260" fill="none" stroke={S} strokeWidth="0.5" />
        {Array.from({length:5}).map((_,r) => (
          <rect key={r} x={1338} y={332+r*50} width={64} height={34} fill={A2} stroke={S} strokeWidth="0.3" />
        ))}

        {/* Ground line */}
        <line x1="800" y1="580" x2="1400" y2="580" stroke={A} strokeWidth="0.8" />

        {/* Dimension line left of main building */}
        <line x1="884" y1="40" x2="884" y2="580" stroke={S} strokeWidth="0.5" />
        <line x1="880" y1="40" x2="888" y2="40" stroke={S} strokeWidth="0.5" />
        <line x1="880" y1="580" x2="888" y2="580" stroke={S} strokeWidth="0.5" />
        <text x="870" y="315" fontSize="8" fontFamily="monospace" fill={S} transform="rotate(-90 870 315)">ELEVATION</text>

        {/* Blueprint label */}
        <text x="905" y="28" fontSize="8" fontFamily="monospace" fill={A} letterSpacing="2">BUILDING A · FRONT ELEVATION</text>

        {/* Partial floor plan bottom-left */}
        <g stroke={S} strokeWidth="0.5" fill="none" opacity="0.7">
          <rect x="100" y="480" width="320" height="120" />
          <line x1="180" y1="480" x2="180" y2="600" />
          <line x1="260" y1="480" x2="260" y2="600" />
          <line x1="340" y1="480" x2="340" y2="600" />
          <line x1="100" y1="540" x2="420" y2="540" />
          {/* door symbol */}
          <line x1="180" y1="540" x2="220" y2="540" stroke={S} strokeWidth="0" />
          <path d="M180 540 A40 40 0 0 1 220 540" fill="none" stroke={S} strokeWidth="0.5" />
        </g>
        <text x="105" y="474" fontSize="8" fontFamily="monospace" fill={S} letterSpacing="1">FLOOR PLAN · LEVEL 01</text>
      </svg>
    </div>
  )

  if (variant === 'skyline') return (
    <div style={style}>
      <svg viewBox="0 0 1400 300" preserveAspectRatio="xMidYMax slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ab-grid-sky" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke={S} strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width="1400" height="300" fill="url(#ab-grid-sky)" />

        {/* Skyline buildings */}
        {[
          {x:80,  y:120, w:70,  h:180},
          {x:170, y:60,  w:100, h:240},
          {x:290, y:140, w:60,  h:160},
          {x:370, y:30,  w:90,  h:270},
          {x:480, y:100, w:80,  h:200},
          {x:580, y:160, w:50,  h:140},
          {x:650, y:50,  w:110, h:250},
          {x:780, y:130, w:70,  h:170},
          {x:870, y:90,  w:90,  h:210},
          {x:990, y:170, w:60,  h:130},
          {x:1070,y:40,  w:100, h:260},
          {x:1200,y:120, w:70,  h:180},
          {x:1290,y:80,  w:90,  h:220},
        ].map(({x,y,w,h},i) => (
          <g key={i}>
            <rect x={x} y={y} width={w} height={300-y} fill="none" stroke={i%3===1?A:S} strokeWidth={i%3===1?0.8:0.5} />
            {/* simplified window dots */}
            {Array.from({length: Math.floor((300-y-20)/18)}).map((_,r) =>
              Array.from({length: Math.floor((w-10)/16)}).map((_,c) => (
                <rect key={`${r}-${c}`} x={x+5+c*16} y={y+10+r*18} width={8} height={10} fill={A2} stroke="none" />
              ))
            )}
          </g>
        ))}

        {/* Ground */}
        <rect x="0" y="295" width="1400" height="5" fill={A2} stroke={A} strokeWidth="0.6" />
      </svg>
    </div>
  )

  if (variant === 'blueprint') return (
    <div style={style}>
      <svg viewBox="0 0 1400 500" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ab-bp" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M30 0H0V30" fill="none" stroke={S} strokeWidth="0.5" />
          </pattern>
          <pattern id="ab-bp2" width="150" height="150" patternUnits="userSpaceOnUse">
            <path d="M150 0H0V150" fill="none" stroke={S} strokeWidth="0.8" />
          </pattern>
        </defs>
        <rect width="1400" height="500" fill="url(#ab-bp)" />
        <rect width="1400" height="500" fill="url(#ab-bp2)" />

        {/* Title block bottom right */}
        <rect x="1050" y="380" width="320" height="100" fill="none" stroke={S} strokeWidth="0.7" />
        <line x1="1050" y1="410" x2="1370" y2="410" stroke={S} strokeWidth="0.5" />
        <line x1="1200" y1="380" x2="1200" y2="480" stroke={S} strokeWidth="0.5" />
        <text x="1060" y="400" fontSize="8" fontFamily="monospace" fill={S}>SMT CONSTRUCTION (P.) LTD</text>
        <text x="1060" y="425" fontSize="7" fontFamily="monospace" fill={S}>BANESHWOR, KATHMANDU</text>
        <text x="1210" y="400" fontSize="7" fontFamily="monospace" fill={S}>DWG NO. SMT-2024</text>
        <text x="1210" y="425" fontSize="7" fontFamily="monospace" fill={S}>SCALE 1:100</text>
        <text x="1060" y="460" fontSize="7" fontFamily="monospace" fill={S}>REV. A · PRELIMINARY</text>

        {/* Ruler top */}
        {Array.from({length:20}).map((_,i) => (
          <g key={i}>
            <line x1={50+i*60} y1="20" x2={50+i*60} y2={i%5===0?35:28} stroke={S} strokeWidth="0.6" />
            {i%5===0 && <text x={46+i*60} y="18" fontSize="7" fontFamily="monospace" fill={S}>{i*5}M</text>}
          </g>
        ))}
        <line x1="50" y1="20" x2="1250" y2="20" stroke={S} strokeWidth="0.5" />

        {/* Ruler left */}
        {Array.from({length:12}).map((_,i) => (
          <g key={i}>
            <line x1="20" y1={50+i*40} x2={i%5===0?38:28} y2={50+i*40} stroke={S} strokeWidth="0.6" />
          </g>
        ))}
        <line x1="20" y1="50" x2="20" y2="500" stroke={S} strokeWidth="0.5" />
      </svg>
    </div>
  )

  if (variant === 'crane') return (
    <div style={style}>
      <svg viewBox="0 0 600 600" preserveAspectRatio="xMaxYMax meet" style={{ position:'absolute', right:0, bottom:0, height:'90%', width:'auto', opacity:0.8 }}>
        <g stroke={A} strokeWidth="1" fill="none">
          {/* mast */}
          <rect x="260" y="100" width="24" height="480" fill={A2} stroke={A} strokeWidth="0.8" />
          {/* lattice */}
          {Array.from({length:13}).map((_,i) => (
            <React.Fragment key={i}>
              <line x1="260" y1={100+i*37} x2="284" y2={100+(i+1)*37} stroke={S} strokeWidth="0.6" />
              <line x1="284" y1={100+i*37} x2="260" y2={100+(i+1)*37} stroke={S} strokeWidth="0.6" />
            </React.Fragment>
          ))}
          {/* jib */}
          <line x1="272" y1="100" x2="560" y2="76" strokeWidth="1.5" />
          <line x1="272" y1="100" x2="80" y2="116" />
          {/* counter weight */}
          <rect x="62" y="113" width="20" height="12" fill={A2} />
          {/* pendants */}
          <line x1="430" y1="84" x2="430" y2="260" strokeWidth="0.6" strokeDasharray="3 2" />
          <rect x="422" y="258" width="18" height="14" fill={A2} />
          {/* hook */}
          <path d="M431 272 Q431 290 424 296 Q417 302 424 310" strokeWidth="0.8" fill="none" />
        </g>
        {/* base */}
        <rect x="240" y="578" width="64" height="16" fill={A2} stroke={A} strokeWidth="0.7" />
      </svg>
    </div>
  )

  return null
}
